import * as CL from '@design-system/component-library';
import { addDays } from 'date-fns';
import { FormProvider, useForm } from 'react-hook-form';
import { dsClass } from '../../common/constants/dsClasses.js';
import { nextMsg, operatorOfSubscriptionMsg, t } from '../../common/i18n/index.js';
import { DatePicker } from '../../common/react-hook-form/components/DatePicker.js';
import { Dropdown } from '../../common/react-hook-form/components/Dropdown.js';
import { SelectRadio } from '../../common/react-hook-form/components/SelectRadio.js';
import { TextInput } from '../../common/react-hook-form/fields/TextInput.js';

import './FixedBroadbandSetupDate.scss';

enum Operator {
  DNA = 'DNA',
  ELISA = 'Elisa',
  TELIA = 'Telia',
}

interface FixedBroadbandSetupDateProps {
  onNext: (values: FixedBroadbandInstallationDetails) => void;
  isMobileProduct: boolean;
  holidays: Date[];
}

export interface FixedBroadbandInstallationDetails {
  installationDate: string;
  existingBroadband: string;
  existingBroadbandOperator?: string;
  existingSubscriptionContractNumber?: string;
  currentOperator: string;
}

export const FixedBroadbandSetupDate = ({ onNext, isMobileProduct, holidays }: FixedBroadbandSetupDateProps) => {
  const methods = useForm<FixedBroadbandInstallationDetails>({
    defaultValues: {
      existingSubscriptionContractNumber: '',
      existingBroadband: '',
      existingBroadbandOperator: '',
      currentOperator: '',
      installationDate: '',
    },
  });

  const [existingBroadband, existingBroadbandOperator] = methods.watch([
    'existingBroadband',
    'existingBroadbandOperator',
  ]);

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onNext)}>
        {isMobileProduct ? null : (
          <>
            <h3 className="ea-h3">
              {t.WLQ1('Do you want to replace the existing broadband connection in the given address?')}
            </h3>
            <SelectRadio
              name="existingBroadband"
              items={[
                {
                  label: t.A37D('No'),
                  value: 'no',
                },
                {
                  label: t.M03S('Yes'),
                  value: 'yes',
                },
              ]}
              required={!isMobileProduct}
            />
          </>
        )}
        {existingBroadband === 'yes' && (
          <>
            <Dropdown
              label={t.A5G4(operatorOfSubscriptionMsg)}
              name="existingBroadbandOperator"
              items={[
                { value: 'elisa', label: Operator.ELISA },
                { value: 'dna', label: Operator.DNA },
                { value: 'telia', label: Operator.TELIA },
                { value: 'other', label: t.C6WY('Other') },
              ]}
            />
            {existingBroadbandOperator === 'other' && (
              <TextInput label="" name="currentOperator" placeholder={t.SKP7('Other operator')} required={true} />
            )}
            <TextInput
              label=""
              name="existingSubscriptionContractNumber"
              placeholder={t.REA8('Contract number')}
              required={true}
            />
          </>
        )}
        <h3 className="ea-h3">{t.RF14('Preferred installation date of subscription')}</h3>
        <div>
          {t.D55A('If installation date is left empty then the broadband will be delivered as soon as possible.')}
        </div>
        <div className={dsClass.PADDING_BOTTOM_4}>
          {t.Z8CO(
            'If the subscription replaces an existing subscription, enter the termination date of the existing subscription here.'
          )}
        </div>
        <div className="of-fixed-broadband-installation-date">
          <DatePicker
            name="installationDate"
            blockedDates={holidays}
            today={addDays(new Date(), 7)}
            disableWeekends
            disableAllPastDays
          />
        </div>
        <CL.Button size="l" className="of-next-button" type="submit">
          {t.F0MY(nextMsg).toUpperCase()}
        </CL.Button>
      </form>
    </FormProvider>
  );
};
